import { Injectable } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { QueryParamsModel } from '../../_base/crud';
import { AppState } from '../../reducers';
import { ServiceCatalogService } from '../_services/service-catalog.service';
import {
  FireServiceCatalogSuccess,
  OneServiceCatalogDeleted,
  OneServiceCatalogOnServerDeleted,
  ServiceCatalogActionToggleLoading,
  ServiceCatalogActionTypes,
  ServiceCatalogCreated,
  ServiceCatalogOnServerCreated,
  ServiceCatalogOnServerUpdated,
  ServiceCatalogsPageCancelled,
  ServiceCatalogsPageLoaded,
  ServiceCatalogsPageRequested,
  ServiceCatalogsPageToggleLoading,
  ServiceCatalogUpdated,
  ThrowServiceCatalogError,
} from '../_actions/service-catalog.actions';

@Injectable()
export class ServiceCatalogEffects {
  showPageLoadingDistpatcher = new ServiceCatalogsPageToggleLoading({ isLoading: true });
  showActionLoadingDistpatcher = new ServiceCatalogActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new ServiceCatalogActionToggleLoading({ isLoading: false });

  @Effect()
  loadServiceCatalogsPage$ = this.actions$.pipe(
    ofType<ServiceCatalogsPageRequested>(ServiceCatalogActionTypes.ServiceCatalogsPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.serviceCatalogService.findServiceCatalogs(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin([requestToServer, lastQuery]).pipe(
        catchError((error) => of([error])),
        map((response) => {
          if (response[0] && response[0].error) {
            if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
            }
            this.store.dispatch(new ThrowServiceCatalogError({ error: response[0] }));
            return new ServiceCatalogsPageCancelled();
          }
          const result = response[0];
          const query: QueryParamsModel = response[1];
          return new ServiceCatalogsPageLoaded({
            serviceCatalogs: result.items,
            totalCount: result.totalCount,
            page: query,
          });
        }),
      );
    }),
  );

  @Effect()
  deleteServiceCatalog$ = this.actions$.pipe(
    ofType<OneServiceCatalogOnServerDeleted>(
      ServiceCatalogActionTypes.OneServiceCatalogOnServerDeleted,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.serviceCatalogService.deleteServiceCatalog(payload.id).pipe(
        catchError((error) => of({ error })),
        map((res) => {
          if (res?.error) {
            return new ThrowServiceCatalogError({ error: res.error });
          }
          this.store.dispatch(this.hideActionLoadingDistpatcher);
          this.store.dispatch(
            new FireServiceCatalogSuccess({
              action_type: ServiceCatalogActionTypes.OneServiceCatalogDeleted,
            }),
          );
          return new OneServiceCatalogDeleted(payload);
        }),
      );
    }),
  );

  @Effect()
  updateServiceCatalog$ = this.actions$.pipe(
    ofType<ServiceCatalogOnServerUpdated>(ServiceCatalogActionTypes.ServiceCatalogOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.serviceCatalogService.updateServiceCatalog(payload.serviceCatalog).pipe(
        catchError((error) => of({ error })),
        map((res: any) => {
          if (res?.error || !res?.id) {
            return new ThrowServiceCatalogError({ error: res?.error || res });
          }
          this.store.dispatch(this.hideActionLoadingDistpatcher);
          this.store.dispatch(
            new FireServiceCatalogSuccess({
              action_type: ServiceCatalogActionTypes.ServiceCatalogUpdated,
            }),
          );
          return new ServiceCatalogUpdated({
            partialServiceCatalog: { id: res.id, changes: res },
            serviceCatalog: res,
          });
        }),
      );
    }),
  );

  @Effect()
  createServiceCatalog$ = this.actions$.pipe(
    ofType<ServiceCatalogOnServerCreated>(ServiceCatalogActionTypes.ServiceCatalogOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.serviceCatalogService.createServiceCatalog(payload.serviceCatalog).pipe(
        catchError((error) => of({ error })),
        map((res: any) => {
          if (res?.error || !res?.id) {
            return new ThrowServiceCatalogError({ error: res?.error || res });
          }
          this.store.dispatch(this.hideActionLoadingDistpatcher);
          this.store.dispatch(
            new FireServiceCatalogSuccess({
              action_type: ServiceCatalogActionTypes.ServiceCatalogCreated,
            }),
          );
          return new ServiceCatalogCreated({ serviceCatalog: res });
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private serviceCatalogService: ServiceCatalogService,
    private store: Store<AppState>,
    private router: Router,
  ) {}
}
